import { logger } from "@-/dev.improve.haniverse.lib.logger";
import * as z from "@-/com.npmjs.zod";
import { deviceAccountStorage } from "#device-settings-store";

export const deviceAccountSchema = z.object({
  // The sync protocol the stored credential was issued for (`ACTOR_SYNC_PROTOCOL`). An account
  // without it predates the gateway protocol and is started over; one with an older number only
  // gets a new credential and keeps its replica.
  actorSyncProtocol: z.number().optional(),
  // Written before moving the guest file, cleared only after replay + cloud push.
  // Lives with the target account so a process restart cannot forget the handoff.
  guestReplayPending: z.boolean().optional(),
  // The account (user id) the local replica belongs to. Signing in again as the same account
  // keeps the replica and whatever it has not uploaded yet.
  ownerId: z.string().optional(),
  // The name of the import a replica rebuild is sending (see `database.md`, "Device: sync
  // supervisor and rebuild"). Written before the replica's unsent changes leave it, cleared once
  // the replica is retired, so a rebuild cut short by a crash is finished at the next open.
  rebuildPending: z.string().optional(),
  // The device's credential for the sync gateway, issued by `database.actor.ensure`.
  tursoDbAuthToken: z.string(),
  // The sync URL is OPTIONAL. In LOCAL DEV the URL is an ephemeral dev-stack loopback port
  // (`http://127.0.0.1:<P+3>`) that changes every stack and goes stale the moment the stack that
  // minted it stops — a persisted dev URL would keep the app pulling from a dead port
  // (`Could not connect to the server`). So dev provisioning stores ONLY the token and the open
  // path resolves the URL from `EXPO_PUBLIC_HANIVERSE_LOCAL_TURSO_SYNC_URL` (inlined from the LIVE stack)
  // instead. In production/staging the URL is server-authoritative (`user_database.tursoDbUrl`) and
  // never changes, so it IS persisted here and read back at open time (env unset → this field wins).
  // Validated when present (must be a real URL); absent is valid (dev, env-sourced).
  tursoDbSyncUrl: z.url().optional(),
});
export type DeviceAccount = z.infer<typeof deviceAccountSchema>;

const ACCOUNT_KEY = "current";

export function getDeviceAccount(): DeviceAccount | null {
  const raw = deviceAccountStorage.getItem(ACCOUNT_KEY);
  if (raw == null) {
    return null;
  }
  const parsed = deviceAccountSchema.safeParse(raw);
  if (parsed.success) {
    return parsed.data;
  }
  logger(import.meta.location).warn(
    "device-account: stored value failed validation, clearing",
    parsed.error,
  );
  deviceAccountStorage.removeItem(ACCOUNT_KEY);
  return null;
}

/**
 * Whose study the local actor database holds: the signed-in account, or this device's own
 * signed-out study. Use it wherever something on the device is kept per account.
 *
 * The actor database's `user` row cannot serve as that key: its id is the same constant in every
 * database, so that two devices never create two of them (see `database.md`, "Clean baseline and
 * identity").
 */
export function getActorOwnerKey(): string {
  return getDeviceAccount()?.ownerId ?? "local";
}

export function setDeviceAccount(account: DeviceAccount | null): void {
  if (account == null) {
    deviceAccountStorage.removeItem(ACCOUNT_KEY);
    return;
  }
  const validated = deviceAccountSchema.parse(account);
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- unstorage sync setItem is typed as never for generic storage
  deviceAccountStorage.setItem(ACCOUNT_KEY, validated as never);
}
